import pb from "../../lib/pb";


/*
--------------------------------------------------
PRODUCT COLLECTION
--------------------------------------------------
*/

const COLLECTION = "products";


/*
--------------------------------------------------
GET PRODUCTS
--------------------------------------------------
*/

export async function getProducts(
    brandId
) {

    if (!brandId) {

        return [];

    }


    return await pb
        .collection(
            COLLECTION
        )
        .getFullList({

            sort:
                "-created",

            filter:
                `brand="${brandId}"`,

            requestKey:
                null

        });

}


/*
--------------------------------------------------
GET SINGLE PRODUCT
--------------------------------------------------
*/

export async function getProduct(
    id
) {

    if (!id) {

        throw new Error(
            "Product ID is required."
        );

    }


    return await pb
        .collection(
            COLLECTION
        )
        .getOne(
            id
        );

}


/*
--------------------------------------------------
CREATE PRODUCT
--------------------------------------------------
*/

export async function createProduct(
    data
) {

    if (!data) {

        throw new Error(
            "Product data is required."
        );

    }


    /*
    PocketBase accepts File objects directly
    inside the create payload.

    This function intentionally does not
    automatically handle product.media.

    Media is uploaded separately through
    uploadProductMedia() after the product
    record has been created.
    */

    return await pb
        .collection(
            COLLECTION
        )
        .create(
            data
        );

}


/*
--------------------------------------------------
UPDATE PRODUCT
--------------------------------------------------
*/

export async function updateProduct(
    id,
    data
) {

    if (!id) {

        throw new Error(
            "Product ID is required."
        );

    }


    if (!data) {

        throw new Error(
            "Product data is required."
        );

    }


    return await pb
        .collection(
            COLLECTION
        )
        .update(
            id,
            data
        );

}


/*
--------------------------------------------------
DELETE PRODUCT
--------------------------------------------------
*/

export async function deleteProduct(
    id
) {

    if (!id) {

        throw new Error(
            "Product ID is required."
        );

    }


    return await pb
        .collection(
            COLLECTION
        )
        .delete(
            id
        );

}


/*
--------------------------------------------------
SUBSCRIBE PRODUCTS
--------------------------------------------------
*/

export async function subscribeProducts(
    brandId,
    callback
) {

    if (!brandId) {

        return null;

    }


    /*
    PocketBase subscribe() returns a Promise
    resolving to the unsubscribe function.

    We await it here so callers receive the
    actual cleanup function.
    */

    const unsubscribe =
        await pb
            .collection(
                COLLECTION
            )
            .subscribe(
                "*",
                event => {

                    const record =
                        event?.record;


                    if (!record) {

                        return;

                    }


                    if (
                        record.brand ===
                        brandId
                    ) {

                        if (
                            typeof callback ===
                            "function"
                        ) {

                            callback(
                                event
                            );

                        }

                    }

                }
            );


    /*
    Return the actual unsubscribe function,
    not the Promise returned by PocketBase.
    */

    return unsubscribe;

}


/*
--------------------------------------------------
UNSUBSCRIBE PRODUCTS
--------------------------------------------------
*/

export async function unsubscribeProducts() {

    await pb
        .collection(
            COLLECTION
        )
        .unsubscribe();

}


/*
--------------------------------------------------
UPLOAD PRODUCT MEDIA
--------------------------------------------------
*/

export async function uploadProductMedia(
    productId,
    files
) {

    if (!productId) {

        throw new Error(
            "Product ID is required."
        );

    }


    /*
    Nothing to upload.
    */

    if (
        !files ||
        !files.length
    ) {

        return await getProduct(
            productId
        );

    }


    /*
    Only accept actual browser File objects.
    */

    const newFiles =
        Array.from(
            files
        ).filter(
            file =>
                file instanceof File
        );


    /*
    Nothing valid was selected.
    */

    if (!newFiles.length) {

        return await getProduct(
            productId
        );

    }


    /*
    IMPORTANT:

    Do NOT read the existing `images` field
    and send the existing filenames together
    with File objects.

    Instead, use PocketBase's `images+`
    file-field modifier.

    This tells PocketBase:

    "Add these files to the existing
    images field."

    Existing images therefore remain intact.
    */

    return await pb
        .collection(
            COLLECTION
        )
        .update(
            productId,
            {
                "images+":
                    newFiles
            }
        );

}


/*
--------------------------------------------------
REMOVE PRODUCT MEDIA
--------------------------------------------------
*/

export async function removeProductMedia(
    productId,
    filename
) {

    if (!productId) {

        throw new Error(
            "Product ID is required."
        );

    }


    if (!filename) {

        throw new Error(
            "Filename is required."
        );

    }


    /*
    PocketBase's `images-` modifier removes
    a specific file from a multi-file field.
    */

    return await pb
        .collection(
            COLLECTION
        )
        .update(
            productId,
            {
                "images-":
                    filename
            }
        );

}


/*
--------------------------------------------------
REPLACE PRODUCT MEDIA
--------------------------------------------------
*/

export async function replaceProductMedia(
    productId,
    files
) {

    if (!productId) {

        throw new Error(
            "Product ID is required."
        );

    }


    /*
    Only actual browser File objects.
    */

    const newFiles =
        Array.from(
            files || []
        ).filter(
            file =>
                file instanceof File
        );


    /*
    Sending `images` replaces the existing
    file field contents with the supplied files.

    If no files are supplied, the existing
    images will be cleared.
    */

    return await pb
        .collection(
            COLLECTION
        )
        .update(
            productId,
            {
                images:
                    newFiles
            }
        );

}